import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, MapPin, Plus, Search } from 'lucide-react';
import {
  CHILD_COPY,
  PICKUP_LOCATION_COPY,
  ROLES,
  ROLE_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  type Role,
  type SubscriptionStatus,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { ReportsSection } from '@/components/ReportsSection';
import { Avatar, Button, Card, Chip, ErrorNote, Field, FieldGroup, Input, SectionTitle, Spinner, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { readLocationOnce } from '@/lib/geolocation';
import { formatFullDate } from '@/lib/format';
import { useRole } from '@/store/auth';
import type { ChildSummary, ClassRoomSummary } from '@/types';

interface FacilityDetail {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  allowPhotoDownload: boolean;
  /** 시설 설정 — 원 위치(등하원 위치 공유의 거리 기준, §5.10 v1.22)만 이 화면이 쓴다 */
  settings?: { location?: { lat: number; lng: number; address?: string } };
  subscription: {
    status: SubscriptionStatus;
    expiresAt: string;
  } | null;
  _count: { children: number; classRooms: number; members: number };
}

interface Member {
  id: string;
  role: Role;
  status: string;
  birthday: string | null;
  user: { id: string; name: string; email: string; phone: string | null };
  teacherAssignments: { classRoom: { id: string; name: string } }[];
}

interface Invite {
  id: string;
  code: string;
  role: Role;
  expiresAt: string;
  child: { id: string; name: string } | null;
}

/** 원장 운영 화면 — 명세서 §7.5 (교사 뷰 + 운영 탭) */
export function OperationsPage() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);

  const billing = useQuery({
    queryKey: ['subscriptions', 'documents'],
    queryFn: () =>
      apiData<
        { id: string; type: 'QUOTE' | 'STATEMENT'; docNo: string; issuedOn: string; amount: number; pdfKey: string | null }[]
      >('/subscriptions/documents'),
  });

  const stats = useQuery({
    queryKey: ['facility', 'stats'],
    queryFn: () =>
      apiData<{
        month: string;
        attendanceRate: number | null;
        dailyNotes: number;
        albums: number;
        medicationsDone: number;
        developmentPublished: number;
        counselingDone: number;
        observations: number;
        messages: number;
      }>('/facilities/current/stats'),
  });

  const facility = useQuery({
    queryKey: ['facility', 'current'],
    queryFn: () => apiData<FacilityDetail>('/facilities/current'),
  });

  const members = useQuery({
    queryKey: ['facility', 'members'],
    queryFn: () => apiData<Member[]>('/facilities/current/members'),
  });

  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const invites = useQuery({
    queryKey: ['facility', 'invites'],
    queryFn: () => apiData<Invite[]>('/facilities/current/invites'),
  });

  const createInvite = useMutation({
    mutationFn: (body: { role: Role; childId?: string }) =>
      apiData<Invite>('/facilities/current/invites', { method: 'POST', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facility', 'invites'] });
    },
  });

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // 클립보드를 못 쓰는 환경에서는 코드가 화면에 이미 보이므로 그대로 둔다.
    }
  }

  if (facility.isPending) return <Spinner />;
  if (facility.isError || !facility.data) {
    return (
      <Screen withNav={false}>
        <AppBar title="앱 관리" back />
        <ErrorNote message="기관 정보를 불러오지 못했어요." onRetry={() => void facility.refetch()} />
      </Screen>
    );
  }

  const data = facility.data;

  return (
    <Screen withNav={false}>
      <AppBar title="앱 관리" subtitle={data.name} back />

      <div className="flex flex-col gap-4 p-4">
        <Card>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="원아" value={data._count.children} />
            <Stat label="반" value={data._count.classRooms} />
            <Stat label="구성원" value={data._count.members} />
          </div>
          {data.subscription && (
            <p className="mt-4 text-sm text-ink-sub">
              구독 {SUBSCRIPTION_STATUS_LABELS[data.subscription.status]} ·{' '}
              {formatFullDate(data.subscription.expiresAt)}까지
            </p>
          )}
          <p className="mt-1 text-sm text-ink-sub">
            앨범 원본 저장 {data.allowPhotoDownload ? '허용' : '차단'} (변경은 시스템 관리자만
            가능해요)
          </p>
        </Card>

        {/* 원 위치 (§5.10 v1.22) — 등하원 위치 공유가 "원까지 거리"를 계산하는 기준 */}
        <FacilityLocationCard
          address={data.address}
          location={data.settings?.location ?? null}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: ['facility', 'current'] })}
        />

        {/* 신고 접수 (§5.30) — 원장이 확인해 숨기거나 안내한 뒤 닫는다 */}
        <ReportsSection />

        {/* 정산 문서 (§5.21 P5) — 관리자가 발행한 견적서·거래명세서를 열람한다 */}
        {(billing.data?.length ?? 0) > 0 && (
          <Card>
            <p className="mb-2 text-sm font-semibold text-ink">정산 문서</p>
            <ul className="flex flex-col gap-2">
              {billing.data?.map((docItem) => (
                <li key={docItem.id} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-medium">
                      {docItem.type === 'QUOTE' ? '견적서' : '거래명세서'} · {docItem.docNo}
                    </p>
                    <p className="text-sm text-ink-sub tnum">
                      {formatFullDate(docItem.issuedOn)} · {docItem.amount.toLocaleString('ko-KR')}원
                    </p>
                  </div>
                  {docItem.pdfKey && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void apiData<{ url: string }>(`/subscriptions/documents/${docItem.id}/pdf`).then(
                          (result) => window.open(result.url, '_blank'),
                        )
                      }
                    >
                      PDF
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* 시설 통계 대시보드 (§3.2, P4) — 판정이 아니라 이번 달 현황이다 */}
        {stats.data && (
          <Card>
            <p className="mb-2 text-sm font-semibold text-ink">이번 달 흐름</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="출석률" value={stats.data.attendanceRate ?? 0} suffix="%" />
              <Stat label="알림장" value={stats.data.dailyNotes} />
              <Stat label="앨범" value={stats.data.albums} />
              <Stat label="투약 완료" value={stats.data.medicationsDone} />
              <Stat label="발달 공개" value={stats.data.developmentPublished} />
              <Stat label="면담 완료" value={stats.data.counselingDone} />
              <Stat label="관찰기록" value={stats.data.observations} />
              <Stat label="쪽지" value={stats.data.messages} />
            </div>
          </Card>
        )}

        <section>
          <SectionTitle>반 {rooms.data?.length ?? 0}개</SectionTitle>
          <ul className="flex flex-col gap-2">
            {rooms.data?.map((room) => (
              <Card key={room.id} as="li" className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{room.name}</p>
                  <p className="truncate text-sm text-ink-sub">
                    담임 {room.teachers.map((t) => t.member.user.name).join(', ') || '미배정'}
                  </p>
                </div>
                <Chip tone="primary">원생 {room._count?.enrollments ?? 0}</Chip>
                <Chip tone="neutral">담임 {room.teachers.length}</Chip>
              </Card>
            ))}
          </ul>
        </section>

        <section>
          <SectionTitle>
            교직원 {(members.data ?? []).filter((m) => m.role !== ROLES.PARENT).length}명
          </SectionTitle>
          <ul className="flex flex-col gap-2">
            {members.data
              ?.filter((member) => member.role !== ROLES.PARENT)
              .map((member) => (
                <Card key={member.id} as="li" className="flex items-center gap-3">
                  <Avatar name={member.user.name} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{member.user.name}</p>
                    <p className="truncate text-sm text-ink-sub">
                      {member.teacherAssignments.map((a) => a.classRoom.name).join(', ') ||
                        member.user.email}
                    </p>
                  </div>
                  <Chip tone="neutral">{ROLE_LABELS[member.role]}</Chip>
                </Card>
              ))}
          </ul>
        </section>

        {/* 교직원 생일 챙기기 (§5.25) — 연도는 받지 않는다 */}
        <BirthdaySection members={(members.data ?? []).filter((m) => m.role !== ROLES.PARENT)} />

        <section>
          <SectionTitle
            action={
              <Button
                size="sm"
                variant="outline"
                icon={<Plus size={15} />}
                loading={createInvite.isPending}
                onClick={() => createInvite.mutate({ role: ROLES.TEACHER })}
              >
                교사 초대
              </Button>
            }
          >
            초대코드
          </SectionTitle>

          {/* 초대→가입 흐름 안내 — 원이 초대하고, 받은 쪽이 가입하며 수락하면 연결이 끝난다 */}
          <p className="mb-2 text-sm text-ink-sub">
            코드를 문자·카카오톡으로 전달하면, 보호자·교사가 가입하며 코드를 입력하는 것으로
            초대를 수락한 거예요. 연결까지 자동으로 끝납니다.
          </p>

          {invites.data?.length === 0 && (
            <Card>
              <p className="text-base text-ink-sub">아직 발급한 초대코드가 없어요.</p>
            </Card>
          )}

          <ul className="flex flex-col gap-2">
            {invites.data?.map((invite) => (
              <Card key={invite.id} as="li" className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="tnum truncate text-lg font-bold tracking-widest">{invite.code}</p>
                  <p className="truncate text-sm text-ink-sub">
                    {ROLE_LABELS[invite.role]}
                    {invite.child ? ` · ${invite.child.name}` : ''} ·{' '}
                    {formatFullDate(invite.expiresAt)}까지
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  icon={<Copy size={15} />}
                  onClick={() => void copy(invite.code)}
                >
                  {copied === invite.code ? '복사됨' : '복사'}
                </Button>
              </Card>
            ))}
          </ul>
        </section>

        <ChildrenSection
          childrenList={children.data ?? []}
          rooms={rooms.data ?? []}
          invites={invites.data ?? []}
          onInvite={(childId) => createInvite.mutate({ role: ROLES.PARENT, childId })}
          invitePending={createInvite.isPending}
          copied={copied}
          onCopy={(code) => void copy(code)}
        />
      </div>
    </Screen>
  );
}

type ChildLinkStatus = 'LINKED' | 'INVITED' | 'UNLINKED';

const LINK_STATUS_META: Record<ChildLinkStatus, { label: string; tone: 'primary' | 'info' | 'neutral' }> = {
  LINKED: { label: '연결 완료', tone: 'primary' },
  INVITED: { label: '초대 중', tone: 'info' },
  UNLINKED: { label: '미연결', tone: 'neutral' },
};

/**
 * 원아 관리 (§5.2) — 현장 피드백 반영: 이름 검색, 반 필터, 보호자 연결 상태
 * (연결 완료·초대 중·미연결) 구분과 반 변경·퇴소까지 이 화면에서 처리한다.
 * 초대 흐름: 원이 초대코드를 보내고, 보호자가 가입하며 코드를 입력해 수락하면 연결 완료.
 */
function ChildrenSection({
  childrenList,
  rooms,
  invites,
  onInvite,
  invitePending,
  copied,
  onCopy,
}: {
  childrenList: ChildSummary[];
  rooms: ClassRoomSummary[];
  invites: Invite[];
  onInvite: (childId: string) => void;
  invitePending: boolean;
  copied: string | null;
  onCopy: (code: string) => void;
}) {
  const queryClient = useQueryClient();
  const isDirector = useRole() === ROLES.DIRECTOR;
  const [q, setQ] = useState('');
  const [roomFilter, setRoomFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ChildLinkStatus | null>(null);
  const [manageId, setManageId] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState('');

  const inviteByChild = new Map(
    invites.filter((invite) => invite.child).map((invite) => [invite.child!.id, invite]),
  );
  const statusOf = (child: ChildSummary): ChildLinkStatus =>
    (child._count?.guardians ?? 0) > 0
      ? 'LINKED'
      : inviteByChild.has(child.id)
        ? 'INVITED'
        : 'UNLINKED';

  const refresh = () => {
    setManageId(null);
    void queryClient.invalidateQueries({ queryKey: ['children'] });
    void queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    void queryClient.invalidateQueries({ queryKey: ['facility'] });
  };

  const move = useMutation({
    mutationFn: ({ childId, classRoomId }: { childId: string; classRoomId: string }) =>
      apiData(`/children/${childId}`, { method: 'PATCH', body: { classRoomId } }),
    onSuccess: refresh,
  });

  const withdraw = useMutation({
    mutationFn: (childId: string) =>
      apiData(`/children/${childId}/withdraw`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  const [registering, setRegistering] = useState(false);

  const keyword = q.trim();
  const filtered = childrenList.filter(
    (child) =>
      (!keyword || child.name.includes(keyword)) &&
      (!roomFilter || child.enrollments?.[0]?.classRoom.id === roomFilter) &&
      (!statusFilter || statusOf(child) === statusFilter),
  );
  const countOf = (status: ChildLinkStatus) =>
    childrenList.filter((child) => statusOf(child) === status).length;

  return (
    <section>
      <SectionTitle
        action={
          isDirector && (
            <Button size="sm" variant="outline" icon={<Plus size={15} />} onClick={() => setRegistering((v) => !v)}>
              {registering ? '닫기' : CHILD_COPY.registerTitle}
            </Button>
          )
        }
      >
        원아 {childrenList.length}명
      </SectionTitle>

      {registering && (
        <ChildRegisterForm
          rooms={rooms}
          onDone={() => {
            setRegistering(false);
            refresh();
          }}
        />
      )}

      <div className="mb-2 flex items-center gap-2 rounded-sm border border-line-strong bg-surface px-3">
        <Search size={16} className="shrink-0 text-ink-sub" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="원아 이름 검색"
          className="h-10 min-w-0 flex-1 bg-transparent text-base outline-none"
        />
      </div>

      {/* 반 필터 + 연결 상태 필터 — 키즈노트류 원생 관리의 전체반/상태 탭에 해당 */}
      <div className="mb-2 flex flex-wrap gap-1.5">
        <Chip
          tone={roomFilter === null ? 'primary' : 'neutral'}
          selected={roomFilter === null}
          onClick={() => setRoomFilter(null)}
        >
          전체 반
        </Chip>
        {rooms.map((room) => (
          <Chip
            key={room.id}
            tone={roomFilter === room.id ? 'primary' : 'neutral'}
            selected={roomFilter === room.id}
            onClick={() => setRoomFilter((prev) => (prev === room.id ? null : room.id))}
          >
            {room.name}
          </Chip>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {(Object.keys(LINK_STATUS_META) as ChildLinkStatus[]).map((status) => (
          <Chip
            key={status}
            tone={statusFilter === status ? 'primary' : 'neutral'}
            selected={statusFilter === status}
            onClick={() => setStatusFilter((prev) => (prev === status ? null : status))}
          >
            {LINK_STATUS_META[status].label} {countOf(status)}
          </Chip>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card>
          <p className="text-base text-ink-sub">조건에 맞는 원아가 없어요.</p>
        </Card>
      )}

      <ul className="flex flex-col gap-2 pb-6">
        {filtered.map((child) => {
          const status = statusOf(child);
          const invite = inviteByChild.get(child.id);
          const roomName = child.enrollments?.[0]?.classRoom.name;
          const managing = manageId === child.id;
          return (
            <Card key={child.id} as="li" className="p-0">
              <div className="flex items-center gap-3 p-3">
                <Link to={`/children/${child.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar name={child.name} size={40} />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{child.name}</p>
                    <p className="truncate text-sm text-ink-sub">
                      {roomName ? `${roomName} · ` : ''}
                      {child.age.label}
                      {status === 'LINKED' ? ` · 보호자 ${child._count?.guardians}명` : ''}
                    </p>
                  </div>
                </Link>
                <Chip tone={LINK_STATUS_META[status].tone}>{LINK_STATUS_META[status].label}</Chip>
              </div>

              <div className="flex items-center gap-2 border-t border-line px-3 py-2">
                {status === 'INVITED' && invite ? (
                  <>
                    <p className="tnum min-w-0 flex-1 truncate text-sm text-ink-sub">
                      초대코드 <b className="tracking-widest text-ink">{invite.code}</b> ·{' '}
                      {formatFullDate(invite.expiresAt)}까지
                    </p>
                    <Button size="sm" variant="outline" icon={<Copy size={14} />} onClick={() => onCopy(invite.code)}>
                      {copied === invite.code ? '복사됨' : '복사'}
                    </Button>
                  </>
                ) : (
                  <div className="flex-1">
                    {status === 'UNLINKED' && (
                      <Button size="sm" variant="outline" loading={invitePending} onClick={() => onInvite(child.id)}>
                        보호자 초대
                      </Button>
                    )}
                  </div>
                )}
                {isDirector && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setManageId(managing ? null : child.id);
                      setMoveTarget('');
                    }}
                  >
                    {managing ? '닫기' : '관리'}
                  </Button>
                )}
              </div>

              {/* 반 변경·퇴소 (§5.2) — 편성 이력은 보존되고, 퇴소 시 서류함은 퇴소 서류철로 전환(§5.22) */}
              {managing && (
                <div className="flex flex-wrap items-center gap-2 border-t border-line bg-bg px-3 py-2.5">
                  {status === 'LINKED' && (
                    <Button size="sm" variant="outline" loading={invitePending} onClick={() => onInvite(child.id)}>
                      보호자 추가 초대
                    </Button>
                  )}
                  <select
                    value={moveTarget}
                    onChange={(e) => setMoveTarget(e.target.value)}
                    className="h-9 min-w-0 flex-1 rounded-sm border border-line-strong bg-surface px-2 text-sm"
                  >
                    <option value="">반 이동 선택…</option>
                    {rooms
                      .filter((room) => room.id !== child.enrollments?.[0]?.classRoom.id)
                      .map((room) => (
                        <option key={room.id} value={room.id}>
                          {room.name}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!moveTarget}
                    loading={move.isPending}
                    onClick={() => move.mutate({ childId: child.id, classRoomId: moveTarget })}
                  >
                    반 변경
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={withdraw.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `${child.name} 어린이를 퇴소 처리할까요?\n서류함은 퇴소 서류철로 전환되고, 쌓인 기록의 열람은 계속 보장돼요.`,
                        )
                      ) {
                        withdraw.mutate(child.id);
                      }
                    }}
                  >
                    퇴소
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </ul>
    </section>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="rounded-sm bg-primary-soft px-2 py-3">
      <p className="tnum text-2xl font-bold text-primary-strong">
        {value}
        {suffix && <span className="text-base font-medium">{suffix}</span>}
      </p>
      <p className="text-sm text-ink-sub">{label}</p>
    </div>
  );
}

/**
 * 교직원 생일 (§5.25) — "MM-DD"만 받아 원장이 생일을 챙긴다.
 * 이번 달 생일에는 케이크 표시가 붙고, 다가오는 순서로 정렬한다.
 */
function BirthdaySection({ members }: { members: Member[] }) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const thisMonth = String(new Date().getMonth() + 1).padStart(2, '0');

  const save = useMutation({
    mutationFn: ({ memberId, birthday }: { memberId: string; birthday: string }) =>
      apiData(`/facilities/current/members/${memberId}/birthday`, {
        method: 'PATCH',
        body: { birthday },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['facility', 'members'] }),
  });

  const daysUntil = (birthday: string) => {
    const now = new Date();
    const [m, d] = birthday.split('-').map(Number);
    const next = new Date(now.getFullYear(), m - 1, d);
    if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
      next.setFullYear(next.getFullYear() + 1);
    }
    return Math.round((next.getTime() - now.getTime()) / 86_400_000);
  };

  const sorted = [...members].sort((a, b) => {
    if (!a.birthday) return 1;
    if (!b.birthday) return -1;
    return daysUntil(a.birthday) - daysUntil(b.birthday);
  });

  return (
    <section>
      <SectionTitle>교직원 생일</SectionTitle>
      <ul className="flex flex-col gap-2">
        {sorted.map((member) => {
          const draft = drafts[member.id] ?? member.birthday ?? '';
          const isBirthdayMonth = member.birthday?.startsWith(thisMonth);
          return (
            <Card key={member.id} as="li" className="flex items-center gap-2">
              <p className="min-w-0 flex-1 truncate font-medium">
                {member.user.name}
                {isBirthdayMonth && ' 🎂'}
                {member.birthday && (
                  <span className="ml-1.5 text-sm font-normal text-ink-sub">
                    D-{daysUntil(member.birthday)}
                  </span>
                )}
              </p>
              <input
                value={draft}
                placeholder="MM-DD"
                className="h-9 w-24 rounded-sm border border-line-strong bg-surface px-2 text-center"
                onChange={(e) =>
                  setDrafts((prev) => ({ ...prev, [member.id]: e.target.value }))
                }
              />
              <Button
                size="sm"
                variant="outline"
                loading={save.isPending}
                disabled={draft === (member.birthday ?? '')}
                onClick={() => save.mutate({ memberId: member.id, birthday: draft })}
              >
                저장
              </Button>
            </Card>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * 원아 등록 (§5.2 v1.22) — 원장 전용. 등록 후 보호자 초대코드를 발급하면 연결이 끝난다.
 * 주소는 비상 연락·등하원 안내용으로 함께 받되 필수는 아니다(보호자가 나중에 고칠 수 있다).
 */
function ChildRegisterForm({ rooms, onDone }: { rooms: ClassRoomSummary[]; onDone: () => void }) {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [sex, setSex] = useState<string>('');
  const [classRoomId, setClassRoomId] = useState(rooms[0]?.id ?? '');
  const [address, setAddress] = useState('');
  const [allergies, setAllergies] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () =>
      apiData<ChildSummary>('/children', {
        method: 'POST',
        body: {
          name: name.trim(),
          birthDate,
          sex: sex || undefined,
          classRoomId: classRoomId || undefined,
          address: address.trim() || undefined,
          allergies: allergies
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          notes: notes.trim() || undefined,
        },
      }),
    onSuccess: (child) => {
      setMessage(CHILD_COPY.registered(child.name));
      setError(null);
      onDone();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '등록하지 못했어요.'),
  });

  return (
    <Card className="mb-3 flex flex-col gap-3">
      <Field label="이름" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 김새싹" />
      </Field>
      <Field label="생년월일" required>
        <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
      </Field>
      <FieldGroup label="성별">
        <div className="flex gap-2">
          {['남', '여'].map((option) => (
            <Chip key={option} tone={sex === option ? 'primary' : 'neutral'} selected={sex === option} onClick={() => setSex(sex === option ? '' : option)}>
              {option}
            </Chip>
          ))}
        </div>
      </FieldGroup>
      <Field label="반">
        <select
          value={classRoomId}
          onChange={(e) => setClassRoomId(e.target.value)}
          className="h-11 w-full rounded-sm border border-line-strong bg-surface px-3 text-base"
        >
          <option value="">나중에 편성</option>
          {rooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={CHILD_COPY.addressLabel} hint={CHILD_COPY.addressHint}>
        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={CHILD_COPY.addressPlaceholder} />
      </Field>
      <Field label="알레르기" hint="쉼표로 구분해요. 식단표와 자동으로 대조돼요.">
        <Input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="예: 우유, 계란" />
      </Field>
      <Field label="특이사항">
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      {error && <p className="text-sm text-notice">{error}</p>}
      {message && <p className="text-sm text-primary-strong">{message}</p>}
      <Button block disabled={!name.trim() || !birthDate} loading={register.isPending} onClick={() => register.mutate()}>
        등록
      </Button>
    </Card>
  );
}

/**
 * 원 위치 (§5.10 v1.22) — 주소 검색(네이버 지오코딩, 서버 키 필요)이나 지금 있는 위치로 잡는다.
 * 등하원 위치 공유 화면이 "원까지 약 1.2km" 같은 안내를 만들 때 쓰는 기준점이다.
 */
function FacilityLocationCard({
  address,
  location,
  onSaved,
}: {
  address: string | null;
  location: { lat: number; lng: number; address?: string } | null;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(address ?? '');
  const [results, setResults] = useState<{ roadAddress: string | null; jibunAddress: string | null; lat: number; lng: number }[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: (q: string) =>
      apiData<{ roadAddress: string | null; jibunAddress: string | null; lat: number; lng: number }[]>(
        `/bus/geocode?query=${encodeURIComponent(q)}`,
      ),
    onSuccess: (rows) => {
      setResults(rows);
      setMessage(rows.length === 0 ? '검색 결과가 없어요. 도로명 주소로 입력해 보세요.' : null);
    },
    onError: (caught) => setMessage(caught instanceof ApiError ? caught.message : '주소 검색에 실패했어요.'),
  });

  const save = useMutation({
    mutationFn: (next: { lat: number; lng: number; address?: string }) =>
      apiData('/facilities/current', { method: 'PATCH', body: { settings: { location: next } } }),
    onSuccess: () => {
      setMessage('원 위치를 저장했어요.');
      setResults([]);
      onSaved();
    },
    onError: (caught) => setMessage(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const useCurrent = useMutation({
    mutationFn: async () => {
      const point = await readLocationOnce({ highAccuracy: true });
      return save.mutateAsync({ lat: point.lat, lng: point.lng, address: address ?? undefined });
    },
    onError: (caught) => setMessage(caught instanceof Error ? caught.message : '위치를 읽지 못했어요.'),
  });

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
            <MapPin size={15} /> 원 위치
          </p>
          <p className="mt-0.5 text-sm text-ink-sub">
            {location
              ? `${location.address ?? '좌표'} · ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`
              : `아직 설정 전이에요. ${PICKUP_LOCATION_COPY.title}에서 원까지 거리를 안내하려면 잡아 주세요.`}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
          {open ? '닫기' : location ? '변경' : '설정'}
        </Button>
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="원 주소 (도로명)" className="flex-1" />
            <Button variant="outline" icon={<Search size={15} />} loading={search.isPending} disabled={query.trim().length < 2} onClick={() => search.mutate(query.trim())}>
              검색
            </Button>
          </div>
          {results.map((row) => (
            <button
              key={`${row.lat}-${row.lng}`}
              type="button"
              className="tap rounded-sm border border-line bg-surface px-3 py-2 text-left text-sm"
              onClick={() => save.mutate({ lat: row.lat, lng: row.lng, address: row.roadAddress ?? row.jibunAddress ?? undefined })}
            >
              {row.roadAddress ?? row.jibunAddress}
            </button>
          ))}
          <Button variant="ghost" size="sm" icon={<MapPin size={14} />} loading={useCurrent.isPending || save.isPending} onClick={() => useCurrent.mutate()}>
            지금 있는 위치로 설정
          </Button>
          {message && <p className="text-sm text-ink-sub">{message}</p>}
        </div>
      )}
    </Card>
  );
}
